import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Filter, FileText, Clock, CheckCircle, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWList = () => {
  const navigate = useNavigate();
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchPermits();
  }, [statusFilter, typeFilter]);

  const fetchPermits = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/permits`;
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('permit_type', typeFilter);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermits(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load permits');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Active': 'bg-green-100 text-green-800',
      'Suspended': 'bg-red-100 text-red-800',
      'Closed': 'bg-purple-100 text-purple-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Expired': 'bg-gray-100 text-gray-800',
      'Extension Requested': 'bg-orange-100 text-orange-800',
      'HSE Verified': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'Active') return <CheckCircle className="w-4 h-4" />;
    if (status === 'Suspended' || status === 'Rejected') return <XCircle className="w-4 h-4" />;
    if (status === 'Pending Approval' || status === 'Extension Requested') return <Clock className="w-4 h-4" />;
    if (status === 'Expired') return <AlertTriangle className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Permit to Work</h1>
            <p className="text-gray-600 mt-1">Manage work permits for high-risk activities</p>
          </div>
          <Button
            onClick={() => navigate('/ptw/create')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Permit
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Filters:</span>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Active">Active</option>
                <option value="Closed">Closed</option>
                <option value="Suspended">Suspended</option>
                <option value="Expired">Expired</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Hot Work">Hot Work</option>
                <option value="Confined Space">Confined Space</option>
                <option value="Work at Height">Work at Height</option>
                <option value="Electrical Work">Electrical Work</option>
                <option value="Excavation">Excavation</option>
                <option value="Lifting Operations">Lifting Operations</option>
                <option value="Chemical Handling">Chemical Handling</option>
                <option value="Isolation Permit">Isolation Permit</option>
                <option value="Radiography">Radiography</option>
                <option value="General Maintenance">General Maintenance</option>
              </select>

              {(statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Permits List */}
      <div className="grid grid-cols-1 gap-4">
        {permits.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No permits found</p>
              <Button
                onClick={() => navigate('/ptw/create')}
                className="mt-4"
              >
                Create Your First Permit
              </Button>
            </CardContent>
          </Card>
        ) : (
          permits.map((permit) => (
            <Card
              key={permit.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/ptw/${permit.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {permit.permit_number}
                      </h3>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {permit.permit_type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(permit.status)}`}>
                        {getStatusIcon(permit.status)}
                        {permit.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{permit.work_description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Location:</span>
                        <p className="font-semibold text-gray-900">{permit.location}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Applicant:</span>
                        <p className="font-semibold text-gray-900">{permit.applicant_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Start Date:</span>
                        <p className="font-semibold text-gray-900">
                          {new Date(permit.work_start_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">End Date:</span>
                        <p className="font-semibold text-gray-900">
                          {new Date(permit.work_end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {permit.contractor_company && (
                      <div className="mt-3">
                        <span className="text-sm text-gray-500">Contractor: </span>
                        <span className="text-sm font-semibold text-gray-900">{permit.contractor_company}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default PTWList;
